import { IMG_CACHE } from '../../Constant/cache'

// Count of cached items added since last cleanup
let addCounter = 0

const hasCacheAPI = () =>
  typeof window !== 'undefined' && 'caches' in window && window.caches

export type ImageProps = {
  src: string
  alt: string
  className?: string
}

const openImgCache = async (): Promise<Cache | null> => {
  if (!hasCacheAPI()) return null
  try {
    return await window.caches.open(IMG_CACHE.name)
  } catch (e) {
    console.error('Failed to open cache:', e)
    return null
  }
}

/**
 * Validates that a cached response is suitable for blob URL creation
 */
const validateCachedResponse = (response: Response): boolean => {
  try {
    // Check response status
    if (!response.ok) {
      return false
    }

    // Check content type is image
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.startsWith('image/')) {
      return false
    }

    // Check if response has readable body
    if (!response.body || response?.bodyUsed) {
      return false
    }

    return true
  } catch (error) {
    return false
  }
}

/**
 * Retrieves an image Response object from the cache.
 * Returns null if not found, expired, or corrupted.
 */
export const getFromImgCache = async (url: string): Promise<Response | null> => {
  try {
    const cache = await openImgCache()
    if (!cache) return null
    
    const cachedResponse = await cache.match(url)
    if (!cachedResponse) {
      return null
    }

    // Validate response integrity for blob URL creation
    if (!validateCachedResponse(cachedResponse)) {
      console.warn('Cached response is corrupted or invalid, removing from cache:', url)
      await cache.delete(url)
      return null
    }

    const timestampHeader = cachedResponse.headers.get(IMG_CACHE.timestampKey)
    if (timestampHeader) {
      const timestamp = parseInt(timestampHeader, 10)
      if (isNaN(timestamp)) {
        // Invalid timestamp, consider expired
        await cache.delete(url)
        return null
      }
      
      const expirationTime = timestamp + IMG_CACHE.expirationDays * 24 * 60 * 60 * 1000
      if (Date.now() > expirationTime) {
        await cache.delete(url)
        return null
      }
    }

    return cachedResponse
  } catch (error) {
    console.error('Error retrieving from cache:', error)
    return null
  }
}

/**
 * Adds an image Response to the cache with a timestamp.
 * Only caches valid image responses suitable for blob URL creation.
 */
export const addToImgCache = async (url: string, response: Response): Promise<void> => {
  try {
    const cache = await openImgCache()
    if (!cache) return

    // Validate response before caching
    if (!validateCachedResponse(response)) {
      console.warn('Skipping cache for invalid response:', url)
      return
    }
    
    const responseToCache = response.clone()

    // Ensure we have a valid clone
    if (!responseToCache.body) {
      console.warn('Response clone has no body, skipping cache:', url)
      return
    }

    const headers = new Headers(responseToCache.headers)
    headers.set(IMG_CACHE.timestampKey, Date.now().toString())

    const responseWithTimestamp = new Response(responseToCache.body, {
      status: responseToCache.status,
      statusText: responseToCache.statusText,
      headers: headers,
    })

    await cache.put(url, responseWithTimestamp)
    addCounter++
    if (addCounter >= IMG_CACHE.cleanFrequency) {
      await cleanImgCache()
      addCounter = 0
    }
  } catch (error) {
    console.error('Error adding to cache:', error)
  }
}

/**
 * Cleans the cache by removing expired items and enforcing size limits.
 */
const cleanImgCache = async (): Promise<void> => {
  try {
    const cache = await openImgCache()
    if (!cache) return
    const keys = await cache.keys()
    const cacheEntries: { url: string; timestamp: number }[] = []

    for (const request of keys) {
      const response = await cache.match(request)
      if (response) {
        const timestampHeader = response.headers.get(IMG_CACHE.timestampKey)
        if (timestampHeader) {
          const timestamp = parseInt(timestampHeader, 10)
          const expirationTime =
            timestamp + IMG_CACHE.expirationDays * 24 * 60 * 60 * 1000

          if (Date.now() > expirationTime) {
            // Delete expired entry
            await cache.delete(request.url)
          } else {
            cacheEntries.push({ url: request.url, timestamp })
          }
        }
      }
    }

    if (cacheEntries.length > IMG_CACHE.maxSize) {
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp)

      const entriesToDelete = cacheEntries.length - IMG_CACHE.maxSize

      for (let i = 0; i < entriesToDelete; i++) {
        await cache.delete(cacheEntries[i].url)
      }
    }
  } catch (error) {
    console.error('Error cleaning cache:', error)
  }
}
